import React, { useEffect, useState } from "react";
import { api, useAuth } from "../lib/api";
import { Users as UsersIcon, KeyRound, ShieldCheck, Loader2 } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resettingUser, setResettingUser] = useState("");
  const [confirmUser, setConfirmUser] = useState(null);
  const [feedback, setFeedback] = useState(null); // { type: 'ok'|'err', text }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setFeedback({ type: "err", text: e.response?.data?.detail || "Kullanıcılar yüklenemedi" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  const reset = async (username) => {
    setResettingUser(username);
    setFeedback(null);
    try {
      const { data } = await api.post(`/users/${encodeURIComponent(username)}/reset-password`);
      setFeedback({
        type: "ok",
        text: `${username} şifresi sıfırlandı. Yeni şifre: ${data.default_password} (kullanıcı ilk girişte yenilemek zorunda).`,
      });
      await load();
    } catch (e) {
      setFeedback({ type: "err", text: e.response?.data?.detail || "Sıfırlanamadı" });
    } finally {
      setResettingUser("");
      setConfirmUser(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="tk-card p-6" data-testid="users-not-allowed">
        <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>
          Bu sayfayı yalnızca admin görüntüleyebilir.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
          <UsersIcon className="w-5 h-5" style={{ color: "hsl(var(--primary))" }} />
        </div>
        <div>
          <h1 className="text-xl font-display font-semibold">Kullanıcılar</h1>
          <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Şifresini unutan kullanıcının şifresini sıfırlayabilirsin.
          </p>
        </div>
      </div>

      {feedback && (
        <div
          data-testid="users-feedback"
          className="text-sm rounded-lg px-3 py-2"
          style={
            feedback.type === "ok"
              ? { backgroundColor: "#E8F5E9", color: "#1B5E20" }
              : { backgroundColor: "#FDECEA", color: "#9B2F28" }
          }
        >
          {feedback.text}
        </div>
      )}

      <div className="tk-card overflow-hidden">
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-sm" style={{ color: "hsl(var(--text-2))" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "hsl(var(--border))" }}>
            {users.map((u) => {
              const isSelf = u.username === "ismet";
              return (
                <li key={u.username} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`user-row-${u.username}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                      {u.role === "admin" ? (
                        <ShieldCheck className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                      ) : (
                        <UsersIcon className="w-4 h-4" style={{ color: "hsl(var(--text-2))" }} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{u.display_name}</div>
                      <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>
                        @{u.username}
                        {u.must_change_password && (
                          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px]" style={{ backgroundColor: "#FFF4E5", color: "#A35200" }}>
                            şifre yenileme bekliyor
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isSelf ? (
                    <span className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>admin</span>
                  ) : (
                    <button
                      data-testid={`reset-password-btn-${u.username}`}
                      onClick={() => setConfirmUser(u)}
                      className="tk-btn-ghost !py-1.5 !px-3 text-xs flex items-center gap-1.5"
                    >
                      <KeyRound className="w-3.5 h-3.5" /> Şifre Sıfırla
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirmUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          data-testid="reset-confirm-modal"
        >
          <div className="tk-card w-full max-w-sm p-5">
            <h3 className="font-display font-semibold text-base mb-2">Şifreyi sıfırla?</h3>
            <p className="text-sm mb-4" style={{ color: "hsl(var(--text-2))" }}>
              <b>{confirmUser.display_name}</b> kullanıcısının şifresi <code>tekne26</code> olarak sıfırlanacak ve ilk girişte yenilemek zorunda kalacak.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmUser(null)}
                className="tk-btn-ghost !py-2 !px-3 text-sm"
                data-testid="reset-confirm-cancel"
              >
                Vazgeç
              </button>
              <button
                onClick={() => reset(confirmUser.username)}
                disabled={resettingUser === confirmUser.username}
                className="tk-btn !py-2 !px-3 text-sm disabled:opacity-60"
                data-testid="reset-confirm-ok"
              >
                {resettingUser === confirmUser.username ? "Sıfırlanıyor..." : "Evet, Sıfırla"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
